import { creditsToUsd, usdToCredits } from './economy/state.js';
import type { EconomyState } from './economy/types.js';

/**
 * The general profile — one client, seen across every realm in a server.
 *
 * A **realm** is one creator's economy inside a Discord server (a `ModelProfile`
 * in the code). Wallets never cross realms, and a realm's own ladder is a
 * battle pass of rewards that assigns no Discord role. What *does* cross is the
 * answer to a different question: how much has this person **spent** with this
 * server's creators, all told? That total drives the one ladder that becomes
 * Discord roles — the global one.
 *
 * **Rank is purchase spend, and nothing else.** Not XP: XP also rises with
 * playing, and a rank that can be bought by spinning is not a record of what a
 * client paid. The total is the sum of each account's `lifetimeSpend`, which only
 * credits tagged `purchase` (a delivered claim, an owner grant she marked as
 * spend) and status she seeded as spend can raise. Wheel wins, events and tier
 * rewards stay spendable and never move anyone up.
 *
 * Pure, like the rest of this package — the server gathers the realms' saves and
 * hands them here, so the same maths can run wherever the saves can be read.
 */

/** The most rungs a ladder may have. A role per rung is a wall past a couple of dozen. */
export const MAX_GLOBAL_TIERS = 20;

/** The most the owner may type for a threshold, so a stray digit cannot overflow it. */
export const MAX_GLOBAL_USD = 1_000_000;

/**
 * One rung of the server-wide ladder.
 *
 * The owner types **dollars**; the threshold the ranking compares against is
 * derived from them with `CREDITS_PER_DOLLAR` and stored beside them. Both are
 * kept so the console can show the owner's number and the computed one without
 * recomputing, and so a later change of the rate cannot silently move anyone: a
 * saved ladder keeps the thresholds it was saved with until it is edited again.
 *
 * Status only — no credit bonus, no coupon. There is no wallet to pay into, and
 * funding it would mean choosing which creator pays.
 */
export interface GlobalTier {
  id: string;
  /** Shown to players, e.g. "Patron". */
  name: string;
  /** Lifetime purchase spend that earns this rung, in dollars. The owner's own number. */
  usd: number;
  /** `usd` in credit-equivalent units — the figure `lifetimeSpend` is compared with. */
  threshold: number;
  /**
   * The Discord role this rung hands out, or null until the owner picks one.
   * Chosen from the server's own roles (see {@link DiscordRoleOption}) — never
   * typed in, since an id pasted one character wrong grants the wrong role
   * silently.
   */
  discordRoleId: string | null;
}

/**
 * A role in the guild, as the bot last reported it.
 *
 * The Activity server holds no Discord credential, by design, so it cannot list
 * a guild's roles itself. The bot — the only process with the token — pushes this
 * catalogue through the rank bridge, and the console's role picker is built from
 * it.
 */
export interface DiscordRoleOption {
  id: string;
  name: string;
  /** `#rrggbb`, or null for the default colour. */
  color: string | null;
  /** False when the bot's own role sits at or below it, so it could not be handed out. */
  assignable: boolean;
}

/**
 * The default server-wide ladder: the same five rungs as before, now in dollars.
 *
 * Roles start unset. Whoever runs the server creates the Discord roles she wants
 * and picks them in the console — Spoyl no longer creates them for her.
 */
export const DEFAULT_GLOBAL_TIERS: GlobalTier[] = [
  { id: 'g-guest', name: 'Guest', usd: 0 },
  { id: 'g-patron', name: 'Patron', usd: 50 },
  { id: 'g-benefactor', name: 'Benefactor', usd: 200 },
  { id: 'g-sovereign', name: 'Sovereign', usd: 600 },
  { id: 'g-mogul', name: 'Mogul', usd: 1500 },
].map((tier) => ({ ...tier, threshold: usdToCredits(tier.usd), discordRoleId: null }));

/** A fresh copy of the default ladder, safe to mutate per guild. */
export function seedGlobalLadder(): GlobalTier[] {
  return DEFAULT_GLOBAL_TIERS.map((tier) => ({ ...tier }));
}

/** The ladder sorted ascending by threshold — the order every reader assumes. */
export function sortedGlobalTiers<T extends { threshold: number }>(ladder: readonly T[]): T[] {
  return [...ladder].sort((a, b) => a.threshold - b.threshold);
}

/**
 * Backfills a persisted ladder.
 *
 * A registry saved before the ladder existed has none and gets the default,
 * which is inert until roles are picked. A ladder saved by the first version —
 * which counted XP and had no `usd` — keeps its thresholds and gains the dollars
 * they correspond to. A damaged one is rebuilt field by field rather than
 * allowed to break the load. An **empty** list is kept as empty: "no rungs" is a
 * state the owner may choose, and it must not be handed the seed back on restart.
 */
export function globalLadderWithDefaults(parsed: unknown): GlobalTier[] {
  if (!Array.isArray(parsed)) return seedGlobalLadder();

  const seen = new Set<string>();
  const tiers: GlobalTier[] = [];
  for (const [index, raw] of parsed.entries()) {
    if (typeof raw !== 'object' || raw === null) continue;
    const value = raw as Partial<GlobalTier> & { xp?: unknown };
    const id = typeof value.id === 'string' && value.id ? value.id : `g${index}`;
    if (seen.has(id)) continue;
    seen.add(id);

    const finite = (input: unknown): number | null =>
      typeof input === 'number' && Number.isFinite(input) && input >= 0 ? input : null;
    // `xp` is the first version's name for the threshold.
    const threshold = finite(value.threshold) ?? finite(value.xp);
    const usd = finite(value.usd) ?? (threshold !== null ? creditsToUsd(threshold) : 0);

    tiers.push({
      id,
      name:
        typeof value.name === 'string' && value.name.trim()
          ? value.name.trim()
          : `Rung ${index + 1}`,
      usd,
      threshold: Math.round(threshold ?? usdToCredits(usd)),
      discordRoleId:
        typeof value.discordRoleId === 'string' && value.discordRoleId ? value.discordRoleId : null,
    });
  }
  return tiers;
}

/** Role ids worth remembering, from untrusted JSON. */
export function roleIdsWithDefaults(parsed: unknown): string[] {
  if (!Array.isArray(parsed)) return [];
  return [
    ...new Set(
      parsed.filter((id): id is string => typeof id === 'string' && /^\d{1,20}$/.test(id)),
    ),
  ];
}

/** The role catalogue from untrusted JSON. */
export function rolesCatalogWithDefaults(parsed: unknown): DiscordRoleOption[] {
  if (!Array.isArray(parsed)) return [];
  const roles: DiscordRoleOption[] = [];
  for (const raw of parsed) {
    if (typeof raw !== 'object' || raw === null) continue;
    const value = raw as Partial<DiscordRoleOption>;
    if (typeof value.id !== 'string' || !/^\d{1,20}$/.test(value.id)) continue;
    roles.push({
      id: value.id,
      name: typeof value.name === 'string' ? value.name.slice(0, 100) : value.id,
      color:
        typeof value.color === 'string' && /^#[0-9a-f]{6}$/i.test(value.color) ? value.color : null,
      assignable: value.assignable === true,
    });
  }
  return roles;
}

/**
 * The rung this much spend holds: the highest one at or below it, or **null**
 * when the client has not reached the lowest. A ladder with no rung at 0 is a
 * legitimate choice — it says "nothing until you have spent this much".
 */
export function globalTierFor<T extends { threshold: number }>(
  ladder: readonly T[],
  total: number,
): T | null {
  let current: T | null = null;
  for (const tier of sortedGlobalTiers(ladder)) {
    if (tier.threshold <= total) current = tier;
  }
  return current;
}

/** The next rung up, or null at the top. */
export function nextGlobalTierFor<T extends { threshold: number }>(
  ladder: readonly T[],
  total: number,
): T | null {
  return sortedGlobalTiers(ladder).find((tier) => tier.threshold > total) ?? null;
}

/**
 * Whether moving from one total to another lands on a different rung. What the
 * server asks before nudging the bot, so a grant that changes nothing visible
 * costs no sync.
 */
export function crossesGlobalTier(
  ladder: readonly GlobalTier[],
  before: number,
  after: number,
): boolean {
  return globalTierFor(ladder, before)?.id !== globalTierFor(ladder, after)?.id;
}

/** What one client has spent in one realm. */
export interface RealmShare {
  profileId: string;
  profileName: string;
  /** Lifetime purchase spend in this realm — the part of the total this creator earned. */
  spend: number;
}

/** One client's general profile: who they are, and what they have spent everywhere. */
export interface ClientTotal {
  /** Discord user id — the key every realm's account shares. */
  playerId: string;
  displayName: string;
  avatar: string | null;
  /** Sum of lifetime purchase spend over every realm the client holds an account in. */
  total: number;
  /** One entry per realm they hold an account in, heaviest first. */
  realms: RealmShare[];
}

/** A global rung as a player may see it: no Discord role id (the bot's), and no dollars. */
export interface PublicGlobalTier {
  id: string;
  name: string;
  /** In the same credit-equivalent units as the realm ladders — never in currency. */
  threshold: number;
}

/**
 * A client's own general profile, as the game receives it.
 *
 * Their own total and their own per-realm share, nothing about anyone else. It
 * is expressed in credit-equivalent **points**, the unit every realm ladder
 * already shows, and never in dollars: Spoyl shows a player no price, and a
 * dollar figure beside a status is one.
 */
export interface GlobalProfileView {
  total: number;
  /** The rung held, or null before the lowest. */
  tier: PublicGlobalTier | null;
  /** The next rung up, or null at the top. */
  next: PublicGlobalTier | null;
  /** The whole ladder, ascending. */
  track: PublicGlobalTier[];
  /** Where the total came from, heaviest realm first. */
  realms: RealmShare[];
}

/** Projects one client's total and the ladder down to what they may be told. */
export function globalProfileView(
  ladder: readonly GlobalTier[],
  client: ClientTotal | null,
): GlobalProfileView {
  const total = client?.total ?? 0;
  const pub = (tier: GlobalTier): PublicGlobalTier => ({
    id: tier.id,
    name: tier.name,
    threshold: tier.threshold,
  });
  const tier = globalTierFor(ladder, total);
  const next = nextGlobalTierFor(ladder, total);
  return {
    total,
    tier: tier ? pub(tier) : null,
    next: next ? pub(next) : null,
    track: sortedGlobalTiers(ladder).map(pub),
    realms: client?.realms ?? [],
  };
}

/** One realm's save, as the aggregation needs it. */
export interface RealmEconomy {
  profileId: string;
  profileName: string;
  state: Pick<EconomyState, 'players'>;
}

/**
 * Joins every realm's accounts on the Discord user id and sums their purchase spend.
 *
 * Read-only over saves that are never merged: the realms keep their separate
 * wallets and ledgers, and this builds a projection on the way out. Attribution
 * cannot be lost here — every unit of the total carries the realm it came from.
 *
 * The display name and avatar are taken from the realm the client was seen in
 * most recently, so a rename shows up without waiting for every realm to hear it.
 */
export function totalsAcrossRealms(realms: readonly RealmEconomy[]): ClientTotal[] {
  const byPlayer = new Map<string, ClientTotal & { seenAt: string }>();

  for (const realm of realms) {
    for (const player of realm.state.players) {
      const spend = Math.max(0, Number.isFinite(player.lifetimeSpend) ? player.lifetimeSpend : 0);
      const seenAt = player.lastSeen ?? '';
      const existing = byPlayer.get(player.id);

      if (!existing) {
        byPlayer.set(player.id, {
          playerId: player.id,
          displayName: player.displayName,
          avatar: player.avatar,
          total: spend,
          realms: [{ profileId: realm.profileId, profileName: realm.profileName, spend }],
          seenAt,
        });
        continue;
      }

      existing.total += spend;
      existing.realms.push({ profileId: realm.profileId, profileName: realm.profileName, spend });
      if (seenAt > existing.seenAt) {
        existing.displayName = player.displayName;
        existing.avatar = player.avatar;
        existing.seenAt = seenAt;
      }
    }
  }

  return [...byPlayer.values()].map(({ seenAt: _seenAt, ...client }) => ({
    ...client,
    realms: [...client.realms].sort((a, b) => b.spend - a.spend),
  }));
}

/** One client's general profile, or null when they have no account anywhere. */
export function totalOf(totals: readonly ClientTotal[], playerId: string): ClientTotal | null {
  return totals.find((client) => client.playerId === playerId) ?? null;
}

/** One rung as the owner submits it. A missing `id` means a new rung. */
export interface GlobalTierEdit {
  id?: string;
  name: string;
  usd: number;
  discordRoleId: string | null;
}

export type GlobalLadderEdit =
  | {
      ok: true;
      ladder: GlobalTier[];
      /**
       * Role ids that used to be rungs and no longer are — removed, or swapped for
       * another role. The bot keeps taking them off people, so a rung the owner
       * deletes does not leave its role on its holders for ever.
       */
      retired: string[];
    }
  | { ok: false; error: string };

/**
 * Validates an edit to the ladder and applies it. The owner may add, remove and
 * retune rungs whenever she likes.
 *
 * Refused outright rather than repaired — a ladder that quietly re-sorted or
 * de-duplicated itself would put people on rungs she did not set:
 *
 * - names are required, dollars are finite and within range, and two rungs may
 *   not share a threshold (the lower of the two would never be held);
 * - a role may serve one rung only, and must be one the bot reported — a role id
 *   cannot be conjured by a request. A role a rung *already holds* is kept even
 *   if it has since left the catalogue, so a deleted role cannot make the whole
 *   ladder uneditable.
 *
 * `retired` is the previous retired list, minus anything now in use, plus the
 * roles this edit freed.
 */
export function editGlobalLadder(
  current: readonly GlobalTier[],
  edits: unknown,
  catalog: readonly DiscordRoleOption[],
  previouslyRetired: readonly string[],
  newId: () => string,
): GlobalLadderEdit {
  if (!Array.isArray(edits)) return { ok: false, error: 'tiers must be a list' };
  if (edits.length > MAX_GLOBAL_TIERS) {
    return { ok: false, error: `A ladder may have at most ${MAX_GLOBAL_TIERS} rungs` };
  }

  const known = new Map(current.map((tier) => [tier.id, tier]));
  const knownRoles = new Set(catalog.map((role) => role.id));
  const used = new Set<string>();
  const thresholds = new Set<number>();
  const ids = new Set<string>();
  const next: GlobalTier[] = [];

  for (const raw of edits) {
    if (typeof raw !== 'object' || raw === null) return { ok: false, error: 'Malformed rung' };
    const edit = raw as Partial<GlobalTierEdit>;

    const name = typeof edit.name === 'string' ? edit.name.trim().slice(0, 40) : '';
    if (!name) return { ok: false, error: 'Every rung needs a name' };

    const usd = edit.usd;
    if (typeof usd !== 'number' || !Number.isFinite(usd) || usd < 0 || usd > MAX_GLOBAL_USD) {
      return {
        ok: false,
        error: `"${name}": the amount must be between $0 and $${MAX_GLOBAL_USD}`,
      };
    }
    const cents = Math.round(usd * 100) / 100;
    const threshold = usdToCredits(cents);
    if (thresholds.has(threshold)) {
      return {
        ok: false,
        error: `Two rungs sit at the same amount ($${cents}) — give each its own`,
      };
    }
    thresholds.add(threshold);

    let id: string;
    if (typeof edit.id === 'string' && edit.id) {
      if (!known.has(edit.id)) {
        return { ok: false, error: 'This ladder changed somewhere else — reload and try again' };
      }
      id = edit.id;
    } else {
      id = newId();
    }
    if (ids.has(id)) return { ok: false, error: 'A rung appears twice' };
    ids.add(id);

    let roleId: string | null = null;
    if (
      edit.discordRoleId !== null &&
      edit.discordRoleId !== undefined &&
      edit.discordRoleId !== ''
    ) {
      if (typeof edit.discordRoleId !== 'string' || !/^\d{1,20}$/.test(edit.discordRoleId)) {
        return { ok: false, error: `"${name}": that is not a Discord role` };
      }
      roleId = edit.discordRoleId;
      const unchanged = known.get(id)?.discordRoleId === roleId;
      if (!unchanged && !knownRoles.has(roleId)) {
        return {
          ok: false,
          error: `"${name}": the bot has not reported that role — create it in Discord, then refresh the list`,
        };
      }
      if (used.has(roleId)) {
        return { ok: false, error: 'One Discord role can only stand for one rung' };
      }
      used.add(roleId);
    }

    next.push({ id, name, usd: cents, threshold, discordRoleId: roleId });
  }

  const freed = current
    .map((tier) => tier.discordRoleId)
    .filter((role): role is string => role !== null && !used.has(role));
  const retired = [...new Set([...previouslyRetired.filter((role) => !used.has(role)), ...freed])];

  return { ok: true, ladder: sortedGlobalTiers(next), retired };
}
